import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  Chip,
  Paper,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Tooltip as _Tooltip,
  Checkbox,
  ListItemText as _ListItemText,
  SelectChangeEvent,
} from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Gavel as GavelIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  TrendingUp as _TrendingUpIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import CaseDialog from '../components/CaseDialog';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import StatusChip from '../components/StatusChip';
import { CaseStatus, getCaseStatusSx, getCaseStatusLabel } from '../utils/caseStatusStyles';


// -------------------- Types ---------------------------------------------
export type Counsel = {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  user?: string;
};

export type Case = {
  id: string;
  lead_defendant: string;
  case_number?: string;
  court: string;
  date_opened?: string | null;
  date_filed?: string | null;
  total_billed: string | null;
  total_paid: string | null;
  status: string;
  created_at: string;
  states?: string[];
  districts?: Array<{ district: string; count: number }>;
  scheme_id?: string;
  scheme?: {
    id: string;
    name: string;
  };
  counsels?: Counsel[];
  counsel_ids?: string[];
  last_updated?: string | null;
  last_update?: {
    display: string;
    full: string;
  } | null;
};

export type Scheme = {
  id: string;
  name: string;
};

const STATUSES = [
  { value: 'IN_REVIEW',           label: 'In Review' },
  { value: 'UNDER_INVESTIGATION', label: 'Under Investigation' },
  { value: 'FILED',               label: 'Filed' },
  { value: 'CLOSED',              label: 'Closed' },
] as const;

// Status color mapping
const _getStatusColor = (status: string): "default" | "primary" | "warning" | "success" | "error" => {
  switch (status) {
    case 'OPEN': return 'primary';
    case 'IN_REVIEW': return 'warning';
    case 'FILED': return 'success';
    case 'CLOSED': return 'default';
    default: return 'default';
  }
};

// -------------------- cell helpers

const dataGridBoldHeaders = {
  backgroundColor: 'white',
  borderRadius: 0,
  border: '1px solid',
  borderColor: 'divider',
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
    backgroundColor: 'action.hover',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    paddingTop: '4px',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    padding: '16px 16px',
    fontSize: '0.9375rem',
  },
  '& .MuiDataGrid-cell:first-of-type': {
    paddingLeft: '24px',
  },
  '& .MuiDataGrid-cell:last-of-type': {
    paddingRight: '24px',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f5f5',
    borderRadius: 0,
    borderBottom: '3px solid #9e9e9e',
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#f5f5f5',
      padding: '16px 16px 12px 16px',
      minHeight: '64px !important',
      height: 'auto !important',
      maxHeight: 'none !important',
    },
    '& .MuiDataGrid-columnHeader:first-of-type': {
      paddingLeft: '24px',
    },
    '& .MuiDataGrid-columnHeader:last-of-type': {
      paddingRight: '24px',
    },
    '& .MuiDataGrid-columnHeaderTitleContainerContent': {
      overflow: 'visible',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      color: 'text.primary',
      fontWeight: 700,
      fontSize: '0.875rem',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      lineHeight: 1.2,
      whiteSpace: 'normal',
      wordWrap: 'break-word',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: '0 4px',
    },
    '& .MuiDataGrid-iconButtonContainer': {
      '& .MuiIconButton-root': {
        color: 'text.secondary',
        '&:hover': {
          backgroundColor: 'action.hover',
          color: 'text.primary',
        },
      },
    },
    '& .MuiDataGrid-sortIcon': {
      color: 'text.secondary',
    },
    '& .MuiDataGrid-menuIcon': {
      color: 'text.secondary',
    },
  },
  '& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderTopColor: 'divider',
    backgroundColor: '#fafafa',
  },
};

// ========================================================================
export default function CaseList() {
  const { access } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [schemes, setSchemes] = useState<Scheme[]>([]);
  const [selectedScheme, setSelectedScheme] = useState<string>('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // Load selected statuses from localStorage on mount
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('caseListStatusFilter');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Stats from API
  const [stats, setStats] = useState({
    total_cases: 0,
    open_cases: 0,
    filed_cases: 0,
    total_billed: 0,
    total_paid: 0,
    suspected_fraud_uncovered: 0
  });

  // ---------- Dialog state ---------------------------------------------
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formValues, setFormValues] = useState<Partial<Case>>({});

  // delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Case | null>(null);

  const headers = { Authorization: `Bearer ${access}` };

  // Use stats from API instead of calculating from paginated results
  const totalCases = stats.total_cases;
  const _openCases = stats.open_cases;
  const filedCases = stats.filed_cases;
  const _totalBilled = stats.total_billed || 0;
  const _totalPaid = stats.total_paid || 0;
  const suspectedFraudUncovered = stats.suspected_fraud_uncovered || 0;

  // Save selected statuses to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('caseListStatusFilter', JSON.stringify(selectedStatuses));
    } catch (err) {
      console.error('Failed to save status filter to localStorage', err);
    }
  }, [selectedStatuses]);

  // --------------------------------------------------------------------
  const fetchCases = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      };
      if (search) params.search = search;

      // Add sorting parameter
      if (sortModel.length > 0) {
        const sort = sortModel[0];
        const orderingField = sort.field;
        params.ordering = sort.sort === 'desc' ? `-${orderingField}` : orderingField;
      }
      if (selectedScheme) params.scheme = selectedScheme;
      // Send multiple statuses as comma-separated string
      if (selectedStatuses.length > 0) {
        params.status = selectedStatuses.join(',');
      }

      const res = await api.get('/cases/', { headers, params });
      // TEMP: hide all cases + summary stats — revert the 3 lines below to:
      //   setCases(res.data.results ?? res.data);
      //   if (res.data.stats) setStats(res.data.stats);
      //   if (res.data.count !== undefined) setRowCount(res.data.count);
      setCases([]);
      setRowCount(0);
    } catch (err) {
      console.error('Failed to fetch cases', err);
      setCases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchemes = async () => {
    try {
      const res = await api.get('/schemes/', { headers });
      setSchemes(res.data.results ?? res.data);
    } catch (err) {
      console.error('Failed to fetch schemes', err);
    }
  };

  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access, selectedScheme, selectedStatuses, paginationModel.page, paginationModel.pageSize, sortModel]);

  useEffect(() => {
    fetchSchemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access]);

  useEffect(() => {
    const state = location.state;
    if (state?.charged_ccns) {
      const prefilledName = state.affiliation_name;
      const today = new Date().toISOString().split('T')[0];
      setFormValues({
        status: 'OPEN',
        lead_defendant: prefilledName,
        date_opened: today,
      });

      setFormMode('create');
      setFormOpen(true);

      // Prevent modal from reopening on re-render
      window.history.replaceState({}, document.title);
    }
  }, []);


  // ---------- Form helpers ---------------------------------------------
  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setFormValues({
      status: 'OPEN',
      date_opened: today
    });
    setFormMode('create');
    setFormOpen(true);
  };

  const openEdit = (row: Case) => {
    setFormValues(row);
    setFormMode('edit');
    setFormOpen(true);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    try {
      if (formMode === 'create') {
        await api.post('/cases/', formValues, { headers });
      } else if (formValues.id) {
        await api.patch(`/cases/${formValues.id}/`, formValues, { headers });
      }
      setFormOpen(false);
      fetchCases();
    } catch (err) {
      console.error('Save case failed', err);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    const deletedCase = deleteTarget;
    try {
      await api.delete(`/cases/${targetId}/`, { headers });
      setDeleteTarget(null);

      // Optimistically remove the case from the UI
      setCases(prevCases => prevCases.filter(c => c.id !== targetId));

      // Optimistically update stats
      setStats(prevStats => {
        const newStats = { ...prevStats };
        newStats.total_cases = Math.max(0, newStats.total_cases - 1);

        // Update status-specific counts
        if (deletedCase.status === 'FILED') {
          newStats.filed_cases = Math.max(0, newStats.filed_cases - 1);
        } else if (deletedCase.status === 'OPEN' || deletedCase.status === 'IN_REVIEW' || deletedCase.status === 'UNDER_INVESTIGATION') {
          newStats.open_cases = Math.max(0, newStats.open_cases - 1);
        }

        // Update fraud amount
        const billedAmount = parseFloat(deletedCase.total_billed ?? '0');
        if (billedAmount > 0) {
          newStats.suspected_fraud_uncovered = Math.max(0, newStats.suspected_fraud_uncovered - billedAmount);
        }

        return newStats;
      });
    } catch (err) {
      console.error('Delete failed', err);
      // If delete failed, refetch to ensure UI is accurate
      fetchCases();
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // ---------- Grid columns ---------------------------------------------
  const columns: GridColDef<Case>[] = [
    // 1. Lead Defendant
    {
      field: 'lead_defendant',
      headerName: 'Lead Defendant',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.9375rem',
            color: 'text.primary',
            letterSpacing: '-0.01em',
            '&:hover': { color: 'primary.main' }
          }}
        >
          {params.value || '-'}
        </Typography>
      )
    },
    // 2. Case Number
    {
      field: 'case_number',
      headerName: 'Case Number',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || '-'}
        </Typography>
      )
    },
    // 3. Counsel - COMMENTED OUT FOR DEMO
    // {
    //   field: 'counsels',
    //   headerName: 'Counsel',
    //   flex: 1.5,
    //   minWidth: 150,
    //   renderCell: (params) => {
    //     const counsels = params.row?.counsels;
    //     if (!counsels || counsels.length === 0) {
    //       return (
    //         <Typography variant="body2" color="text.disabled">
    //           No counsel assigned
    //         </Typography>
    //       );
    //     }
    //
    //     return (
    //       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
    //         {counsels.map((counsel) => (
    //           <Chip
    //             key={counsel.id}
    //             label={counsel.full_name}
    //             size="small"
    //             variant="outlined"
    //             sx={{
    //               height: 22,
    //               fontSize: '0.75rem',
    //               backgroundColor: 'grey.50',
    //               borderColor: 'grey.300',
    //               color: 'text.primary',
    //               fontWeight: 400,
    //             }}
    //           />
    //         ))}
    //       </Box>
    //     );
    //   }
    // },
    // 3. Date Opened - COMMENTED OUT FOR DEMO
    // {
    //   field: 'date_opened',
    //   headerName: 'Date Opened',
    //   flex: 0.8,
    //   minWidth: 120,
    //   renderCell: (params) => {
    //     const raw = params.row?.date_opened;
    //     if (!raw) return '-';
    //     const date = new Date(raw);
    //     return (
    //       <Typography variant="body2" color="text.secondary">
    //         {isNaN(date.getTime())
    //           ? '-'
    //           : date.toLocaleDateString('en-US', {
    //               year: 'numeric',
    //               month: 'short',
    //               day: 'numeric',
    //             })}
    //       </Typography>
    //     );
    //   },
    // },
    // 4. Date Filed
    {
      field: 'date_filed',
      headerName: 'Date Filed',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        const raw = params.row?.date_filed;
        if (!raw) return '-';

        // Parse YYYY-MM-DD format directly without timezone conversion
        const [year, month, day] = raw.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed

        return (
          <Typography variant="body2" color="text.secondary">
            {date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
        );
      },
    },
    // 5. Court
    { 
      field: 'court', 
      headerName: 'Court', 
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || '-'}
        </Typography>
      )
    },
    // 6. Alleged Fraud Amount
    {
      field: 'total_billed',
      headerName: 'Alleged Fraud Amount',
      width: 240,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (value, row) => parseFloat(row.total_billed ?? '0'),
      renderCell: (params) => {
        const val = params.value;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {typeof val === 'number' && !isNaN(val) && val > 0
              ? formatCurrency(val)
              : '-'}
          </Typography>
        );
      },
    },
    // 7. States
    {
      field: 'states',
      headerName: 'States',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const states = params.row?.states;
        if (!states || states.length === 0) {
          return (
            <Typography variant="body2" color="text.disabled">
              -
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {states.map((state) => (
              <Chip
                key={state}
                label={state}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: 'primary.50',
                  borderColor: 'primary.200',
                  color: 'primary.700',
                  fontWeight: 500,
                  '& .MuiChip-label': {
                    padding: '0 6px',
                  }
                }}
              />
            ))}
          </Box>
        );
      }
    },
    // 9. Status
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      minWidth: 140,
      renderCell: (params) => {
        return (
          <StatusChip
            status={params.value as CaseStatus}
            size="small"
            sx={{ minWidth: 90 }}
          />
        );
      }
    },
    // 10. Last Updated - COMMENTED OUT FOR DEMO
    // {
    //   field: 'last_updated',
    //   headerName: 'Last Updated',
    //   width: 130,
    //   renderCell: (params) => {
    //     const raw = params.row?.last_updated;
    //     if (!raw) return (
    //       <Typography variant="body2" color="text.disabled">
    //         -
    //       </Typography>
    //     );
    //     const date = new Date(raw);
    //     return (
    //       <Typography variant="body2" color="text.secondary">
    //         {isNaN(date.getTime())
    //           ? '-'
    //           : date.toLocaleDateString('en-US', {
    //               month: 'short',
    //               day: 'numeric',
    //             })}
    //       </Typography>
    //     );
    //   },
    // },
    // 11. Last Update - COMMENTED OUT FOR DEMO
    // {
    //   field: 'last_update',
    //   headerName: 'Last Update',
    //   flex: 2,
    //   minWidth: 200,
    //   renderCell: (params) => {
    //     const lastUpdate = params.row?.last_update;
    //     if (!lastUpdate) {
    //       return (
    //         <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
    //           No activity
    //         </Typography>
    //       );
    //     }
    //
    //     return (
    //       <Tooltip title={lastUpdate.full} arrow>
    //         <Typography
    //           variant="body2"
    //           color="text.secondary"
    //           sx={{
    //             overflow: 'hidden',
    //             textOverflow: 'ellipsis',
    //             whiteSpace: 'nowrap',
    //             maxWidth: '100%'
    //           }}
    //         >
    //           {lastUpdate.display}
    //         </Typography>
    //       </Tooltip>
    //     );
    //   },
    // },
    // 12. Actions
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(params.row as Case);
            }}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(params.row as Case);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  // ============================ JSX ====================================
  return (
    <Layout>

      {/* Stats Cards */}
      <Box
        display="flex"
        gap={3}
        mb={4}
        sx={{
          flexWrap: 'wrap',
          '& > *': {
            flex: '1 1 250px',
            minWidth: '200px',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Card
          sx={{
            backgroundColor: '#e3f2fd',
            display: 'flex',
            flex: 1,
            borderLeft: '4px solid #1976d2',
            border: '1px solid',
            borderColor: '#bbdefb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            transition: 'all 0.2s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeftColor: '#1565c0',
              transform: 'translateY(-2px)',
            }
          }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flex: 1, py: 3 }}>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                Total Cases
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {totalCases}
              </Typography>
            </Box>
            <GavelIcon sx={{ fontSize: 48, color: '#1976d2', opacity: 0.2 }} />
          </CardContent>
        </Card>

        <Card
          sx={{
            backgroundColor: '#e8f5e9',
            display: 'flex',
            flex: 1,
            borderLeft: '4px solid #2e7d32',
            border: '1px solid',
            borderColor: '#c8e6c9',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            transition: 'all 0.2s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeftColor: '#1b5e20',
              transform: 'translateY(-2px)',
            }
          }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flex: 1, py: 3 }}>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                Filed Cases
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {filedCases}
              </Typography>
            </Box>
            <BusinessIcon sx={{ fontSize: 48, color: '#2e7d32', opacity: 0.2 }} />
          </CardContent>
        </Card>

        <Card
          sx={{
            backgroundColor: '#fff3e0',
            display: 'flex',
            flex: 1,
            borderLeft: '4px solid #ed6c02',
            border: '1px solid',
            borderColor: '#ffe0b2',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            transition: 'all 0.2s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeftColor: '#e65100',
              transform: 'translateY(-2px)',
            }
          }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flex: 1, py: 3 }}>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                Suspected Fraud Uncovered
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {formatCurrency(suspectedFraudUncovered)}
              </Typography>
            </Box>
            <MoneyIcon sx={{ fontSize: 48, color: '#ed6c02', opacity: 0.2 }} />
          </CardContent>
        </Card>

      </Box>

      {/* Search and Actions Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderBottom: '2px solid #e0e0e0',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search cases..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') fetchCases();
              }}
              sx={{
                minWidth: 250,
                backgroundColor: 'background.default',
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Filter by Scheme</InputLabel>
              <Select
                value={selectedScheme}
                onChange={(e) => setSelectedScheme(e.target.value)}
                label="Filter by Scheme"
                sx={{
                  backgroundColor: 'background.default',
                }}
              >
                <MenuItem value="">
                  <em>All Schemes</em>
                </MenuItem>
                {schemes.map((scheme) => (
                  <MenuItem key={scheme.id} value={scheme.id}>
                    {scheme.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                multiple
                value={selectedStatuses}
                onChange={(e: SelectChangeEvent<string[]>) => {
                  const value = e.target.value;
                  setSelectedStatuses(typeof value === 'string' ? value.split(',') : value);
                }}
                label="Filter by Status"
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>All Statuses</em>;
                  }
                  return (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selected.map((val) => (
                        <Chip
                          key={val}
                          label={getCaseStatusLabel(val as CaseStatus)}
                          size="small"
                          sx={{
                            ...getCaseStatusSx(val as CaseStatus),
                            minWidth: 90,
                            height: 24
                          }}
                        />
                      ))}
                    </Box>
                  );
                }}
                sx={{
                  backgroundColor: 'background.default',
                }}
              >
                {STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={selectedStatuses.indexOf(status.value) > -1} />
                    <Chip
                      label={getCaseStatusLabel(status.value as CaseStatus)}
                      size="small"
                      sx={{
                        ...getCaseStatusSx(status.value as CaseStatus),
                        minWidth: 90,
                        ml: 1
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                borderColor: 'primary.main',
              },
            }}
          >
            New Case
          </Button>
        </Box>
      </Paper>

      {/* Data Table */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderTop: 'none',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          mb: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}
      >
        <DataGrid
          rows={cases}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 25, 50]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => setSortModel([...newSortModel])}
          disableRowSelectionOnClick
          disableVirtualization
          autoHeight
          onRowClick={(params) => {
            const caseRow = params.row as Case;
            // Navigate to case scoping page (now the home page for all cases)
            navigate(`/cases/${caseRow.id}`);
          }}
          sx={{
            ...dataGridBoldHeaders,
            width: '100%',
          }}
        />
      </Paper>

      {/* ------------------ Create / Edit Dialog --------------------- */}
      <CaseDialog
        open={formOpen}
        mode={formMode}
        values={formValues}
        onChange={handleFormChange}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />


      {/* ------------------ Delete Confirmation ---------------------- */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Delete this case?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the case "{deleteTarget?.lead_defendant}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}